// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { formatCanonicalUtcDate, normalizeCaseUtcDate, normalizeCell, parseCaseUtcDate, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { normalizeBearerToken } from './CaseBuddyWorkbookState.js';

export function getFirstValidUtcDateFromEntryRows(entryRows = []) {
  for (const entryRow of Array.isArray(entryRows) ? entryRows : []) {
    const info = parseCaseUtcDate(entryRow?.row?.[4]);
    if (info.isValid) return info.canonical;
  }
  return '';
}

export function analyzeEntryUtcDateCompletion(entryRows = []) {
  const meaningfulRows = (Array.isArray(entryRows) ? entryRows : []).filter(entryRow => rowHasMeaningfulData(entryRow?.row));
  const dateInfos = meaningfulRows.map(entryRow => parseCaseUtcDate(entryRow?.row?.[4]));
  const validInfos = dateInfos.filter(info => info.isValid);
  const timestampKeys = new Set(validInfos.map(info => String(info.timestamp)));
  const hasConflict = timestampKeys.size > 1;
  const missingCount = dateInfos.filter(info => !info.hasValue).length;
  const invalidCount = dateInfos.filter(info => info.hasValue && !info.isValid).length;
  const canonicalDate = hasConflict ? '' : (validInfos[0]?.canonical || '');
  const complete = meaningfulRows.length > 0 && missingCount === 0 && invalidCount === 0 && !hasConflict;

  return {
    complete,
    canonicalDate,
    hasConflict,
    missingCount,
    invalidCount,
    meaningfulRowCount: meaningfulRows.length,
    needsDate: !complete
  };
}

export function formatNetworkLookupUtcDate(result = {}) {
  const directDate = normalizeCaseUtcDate(result.utcDate);
  if (directDate) return directDate;

  const unixTimestamp = Number(result.unixTimestamp);
  if (Number.isFinite(unixTimestamp) && unixTimestamp > 0) {
    try {
      return formatCanonicalUtcDate(new Date(unixTimestamp * 1000));
    } catch {
      return '';
    }
  }

  return '';
}

export function buildNetworkLookupAuditRows(rows = [], summary = {}) {
  return [
    ['Case Buddy Network Lookup Audit', '', '', '', '', '', '', '', '', 'Generated by Identify Transaction Networks. Do not append this audit back into Main Data.'],
    ['Checked', summary.requested || 0, 'Resolved', summary.resolved || 0, 'Unresolved', summary.unresolved || 0, 'Normalized Hashes', summary.normalizedHashCount || 0],
    ['Main Row', 'Path Label', 'Transaction Hash', 'To Address Hint', 'Lookup Status', 'Chain Id', 'Chain Name', 'UTC Date', 'Source', 'Message'],
    ...(Array.isArray(rows) ? rows : []).map((row) => {
      const result = row.lookupResult || {};
      const chainId = normalizeCell(result.caseChainId || row.chainId);
      const chainName = normalizeCell(result.caseChainName || row.chainName);
      const utcDate = formatNetworkLookupUtcDate(result);
      return [
        row.spreadsheetRowNumber || '',
        row.pathLabel || '',
        row.hash || '',
        row.toAddressHint || '',
        row.statusLabel || row.status || '',
        chainId,
        chainName,
        utcDate,
        result.source || row.source || '',
        row.message || ''
      ];
    })
  ];
}

export function buildDateLookupAuditRows(rows = [], summary = {}) {
  return [
    ['Case Buddy Date Lookup Audit', '', '', '', '', '', '', '', '', 'Generated by Complete UTC dates. Do not append this audit back into Main Data.'],
    ['Checked', summary.requested || 0, 'Filled', summary.filled || 0, 'Local Propagated', summary.resolvedLocal || 0, 'Lookup Filled', summary.resolvedLookup || 0, 'Unresolved', summary.unresolved || 0],
    ['Main Row', 'Path Label', 'Transaction Hash', 'Current / Applied UTC Date', 'Lookup Status', 'Chain Id', 'Chain Name', 'Source', 'Message'],
    ...(Array.isArray(rows) ? rows : []).map((row) => {
      const result = row.lookupResult || {};
      const utcDate = row.dateToApply || formatNetworkLookupUtcDate(result) || row.currentDateLabel || '';
      const chainId = normalizeCell(result.caseChainId || row.chainId);
      const chainName = normalizeCell(result.caseChainName || row.chainName);
      return [
        row.spreadsheetRowNumber || '',
        row.pathLabel || '',
        row.hash || '',
        utcDate,
        row.statusLabel || row.status || '',
        chainId,
        chainName,
        result.source || row.source || '',
        row.message || ''
      ];
    })
  ];
}

export function buildDateSortAuditRows(originalBlocks = [], sortedBlocks = []) {
  const newPositionById = new Map();
  (Array.isArray(sortedBlocks) ? sortedBlocks : []).forEach((block, index) => {
    newPositionById.set(block.id, index + 1);
  });

  return [
    ['Case Buddy Main Entry Date Sort Audit', '', '', '', '', '', '', '', '', 'Generated by Sort By Date. Main-entry blocks were reordered in Main Data; child/subtransaction rows stayed with their current parent block.'],
    ['Original Main Blocks', originalBlocks.length || 0, 'Sorted Blocks', sortedBlocks.length || 0, 'Undated / Invalid Blocks', originalBlocks.filter(block => !block.dateInfo?.isValid).length],
    ['Old Position', 'New Position', 'Old Row', 'Path Label', 'Transaction Hash', 'UTC Date', 'Block Rows', 'Sort Key Status'],
    ...(Array.isArray(originalBlocks) ? originalBlocks : []).map((block, index) => [
      index + 1,
      newPositionById.get(block.id) || '',
      block.startRow + 1,
      block.pathLabel || '',
      block.hash || '',
      block.dateInfo?.canonical || block.dateInfo?.original || '',
      block.rows?.length || 0,
      block.dateInfo?.isValid ? 'Dated' : 'Undated or invalid'
    ])
  ];
}

export function buildMainPathLabelAuditRows(rows = [], summary = {}) {
  return [
    ['Case Buddy Main Path Label Audit', '', '', '', '', '', '', '', '', 'Generated by Assign main transaction path labels. Do not append this audit back into Main Data.'],
    ['Eligible Main Entries', summary.total || 0, 'Applied', summary.applied || 0, 'Changed', summary.changed || 0, 'Continuation Labels Cleared', summary.continuationCleared || 0],
    ['Order', 'Main Row', 'Old Label', 'New Label', 'Transaction Hash', 'UTC Date', 'To Address', 'Status', 'Message'],
    ...(Array.isArray(rows) ? rows : []).map(row => [
      row.order || '',
      row.spreadsheetRowNumber || '',
      row.currentLabel || '',
      row.proposedLabel || '',
      row.hash || '',
      row.utcDate || '',
      row.toAddress || '',
      row.statusLabel || row.status || '',
      row.message || ''
    ])
  ];
}

export function extractAuthTokenCandidate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const direct = normalizeBearerToken(value);
    if (!direct) return '';
    if (direct.startsWith('{') || direct.startsWith('[')) {
      try {
        return extractAuthTokenCandidate(JSON.parse(direct));
      } catch {
        return '';
      }
    }
    return direct;
  }

  if (typeof value !== 'object') return '';

  const directKeys = ['token', 'authToken', 'bearerToken', 'accessToken', 'access_token', 'jwt', 'jwtToken', 'idToken', 'id_token'];
  for (const key of directKeys) {
    const token = extractAuthTokenCandidate(value[key]);
    if (token) return token;
  }

  for (const key of ['session', 'auth', 'user', 'account', 'currentUser', 'profile']) {
    if (value[key] && value[key] !== value) {
      const token = extractAuthTokenCandidate(value[key]);
      if (token) return token;
    }
  }

  return '';
}

export function collectStoredAuthToken() {
  if (typeof window === 'undefined') return '';
  const keys = [
    'token',
    'authToken',
    'accessToken',
    'access_token',
    'jwt',
    'jwtToken',
    'idToken',
    'id_token',
    'mortalitycore_auth_token',
    'mortalitycore_token',
    'mortalitycore_jwt',
    'mc_auth_token',
    'casebuddy_auth_token',
    'casebuddy_token',
    'casebuddy_jwt',
    'hermes_auth_token'
  ];
  const stores = [];
  try { if (window.localStorage) stores.push(window.localStorage); } catch { /* ignore */ }
  try { if (window.sessionStorage) stores.push(window.sessionStorage); } catch { /* ignore */ }

  for (const store of stores) {
    for (const key of keys) {
      try {
        const token = extractAuthTokenCandidate(store.getItem(key));
        if (token) return token;
      } catch {
        // Ignore inaccessible storage keys.
      }
    }

    try {
      for (let index = 0; index < store.length; index += 1) {
        const key = store.key(index);
        if (!key || !/(token|jwt|auth|session)/i.test(key)) continue;
        const token = extractAuthTokenCandidate(store.getItem(key));
        if (token) return token;
      }
    } catch {
      // Ignore storage enumeration failures.
    }
  }

  return '';
}

export function downloadTextFile(filename, content, mimeType = 'text/plain;charset=utf-8;') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.click();
  URL.revokeObjectURL(url);
}
